// Contract-safe normalization for MessageRenderer.
// Goal: after calling this, you should NEVER touch `raw` again.
// Everything returned has stable types (string / array / object).

export type JsonRecord = Record<string, unknown>

export type NormalizedMessageContent = {
  /** Always a string */
  text: string
  /** Always an array */
  citations: unknown[]
  /** Always an array */
  thinking: unknown[]
  /** Always an array */
  memorySuggestions: unknown[]
  /** Always an object (may be empty) */
  metadata: JsonRecord
  /** Original contentType if available (best-effort) */
  contentType: string
  /** Whether we had any non-empty text */
  hasText: boolean
  /** Always an array */
  searchResults: unknown[]
  /** Always an array */
  cards: unknown[]
  /** Always an array */
  memoryKeys: unknown[]
}

/**
 * Normalize arbitrary message "content" (string | object | null/undefined) into a safe shape.
 * This helper is intentionally strict: it prevents `.length` / `.map()` crashes by
 * guaranteeing stable types for common fields.
 */
export function normalizeMessageContent(raw: unknown): NormalizedMessageContent {
  // Defaults: stable types, safe to read `.length` and call `.map()` on arrays.
  const base: NormalizedMessageContent = {
    text: "",
    citations: [],
    thinking: [],
    memorySuggestions: [],
    metadata: {},
    contentType: "unknown",
    hasText: false,
    searchResults: [],
    cards: [],
    memoryKeys: [],
  }

  // 1) String content (most common)
  if (typeof raw === "string") {
    const text = raw ?? ""
    return {
      ...base,
      text,
      contentType: "string",
      hasText: text.trim().length > 0,
    }
  }

  // 2) Null/undefined/primitive => defaults
  if (!raw || typeof raw !== "object") {
    return base
  }

  // 3) Object content: pick known fields safely
  const obj = raw as JsonRecord

  // Some pipelines send { content: "..." } or { text: "..." }
  const textCandidate =
    typeof obj.text === "string"
      ? obj.text
      : typeof obj.content === "string"
        ? obj.content
        : typeof obj.message === "string"
          ? obj.message
          : ""

  // Citations can be under various keys depending on model contract
  const citationsCandidate = Array.isArray(obj.citations)
    ? obj.citations
    : Array.isArray(obj.sources)
      ? obj.sources
      : Array.isArray(obj.references)
        ? obj.references
        : []

  // Thinking / reasoning traces
  const thinkingCandidate = Array.isArray(obj.thinking)
    ? obj.thinking
    : Array.isArray(obj.reasoning)
      ? obj.reasoning
      : Array.isArray(obj.trace)
        ? obj.trace
        : []

  // Memory suggestions / extractions
  const memorySuggestionsCandidate = Array.isArray(obj.memorySuggestions)
    ? obj.memorySuggestions
    : Array.isArray(obj.suggestedMemories)
      ? obj.suggestedMemories
      : Array.isArray(obj.memory_proposals)
        ? obj.memory_proposals
        : Array.isArray(obj.memories)
          ? obj.memories
          : []

  // Search results
  const searchResultsCandidate = Array.isArray(obj.searchResults) ? obj.searchResults : []

  // Cards
  const cardsCandidate = Array.isArray(obj.cards) ? obj.cards : []

  // Memory keys
  const memoryKeysCandidate = Array.isArray(obj.memoryKeys) ? obj.memoryKeys : []

  // Metadata: only accept plain object; otherwise keep empty object
  const metadataCandidate =
    obj.metadata && typeof obj.metadata === "object" && !Array.isArray(obj.metadata) ? (obj.metadata as JsonRecord) : {}

  const contentTypeCandidate =
    typeof obj.contentType === "string" ? obj.contentType : typeof obj.type === "string" ? obj.type : "object"

  const text = textCandidate ?? ""

  return {
    ...base,
    text,
    citations: citationsCandidate,
    thinking: thinkingCandidate,
    memorySuggestions: memorySuggestionsCandidate,
    metadata: metadataCandidate,
    contentType: contentTypeCandidate,
    hasText: text.trim().length > 0,
    searchResults: searchResultsCandidate,
    cards: cardsCandidate,
    memoryKeys: memoryKeysCandidate,
  }
}

// Optional: assert guard for any derived value before `.length`
export function safeString(x: unknown): string {
  return typeof x === "string" ? x : ""
}

export function safeArray<T = unknown>(x: unknown): T[] {
  return Array.isArray(x) ? (x as T[]) : []
}
